import { createApi } from '@reduxjs/toolkit/query/react';
import { BuildingWithTrusses } from '../../types';
import { baseQueryStub } from '../../utils';
import {
  StageConfigDef,
  TaskTrackerRequest,
  TaskTrackerResponse,
  TrussSpecificationResponse,
} from './types';
import { WorkOrderTravelerService } from './work-order-traveler-service';

let travelerService: WorkOrderTravelerService | null = null;
const getTravelerService = () => {
  travelerService ??= new WorkOrderTravelerService();
  return travelerService;
};

export const workOrderTravelerApi = createApi({
  reducerPath: 'workOrderTravelerApi',
  baseQuery: baseQueryStub,
  tagTypes: ['TaskTracker', 'TrussSpecification', 'TravelerBuildingTrusses', 'StageConfig'],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: builder => ({
    getTaskTrackersByOrder: builder.query<TaskTrackerResponse[], number>({
      queryFn: async orderId => {
        const service = getTravelerService();
        const data = await service.getTaskTrackersByOrder(orderId);
        return { data };
      },
      providesTags: (result, _error, orderId) =>
        result
          ? [
              ...result.map(tt => ({ type: 'TaskTracker' as const, id: tt.id })),
              { type: 'TaskTracker', id: `ORDER-${orderId}` },
            ]
          : [{ type: 'TaskTracker', id: `ORDER-${orderId}` }],
      keepUnusedDataFor: 30,
    }),

    getTaskTrackersByTruss: builder.query<TaskTrackerResponse[], number>({
      queryFn: async trussId => {
        const service = getTravelerService();
        const data = await service.getTaskTrackersByTruss(trussId);
        return { data };
      },
      providesTags: (result, _error, trussId) =>
        result
          ? [
              ...result.map(tt => ({ type: 'TaskTracker' as const, id: tt.id })),
              { type: 'TaskTracker', id: `TRUSS-${trussId}` },
            ]
          : [{ type: 'TaskTracker', id: `TRUSS-${trussId}` }],
      keepUnusedDataFor: 30,
    }),

    createTaskTracker: builder.mutation<TaskTrackerResponse, TaskTrackerRequest>({
      queryFn: async request => {
        const service = getTravelerService();
        const data = await service.createTaskTracker(request);
        return { data };
      },
      invalidatesTags: ['TaskTracker'],
    }),

    updateTaskTracker: builder.mutation<
      TaskTrackerResponse,
      { id: number; request: TaskTrackerRequest }
    >({
      queryFn: async ({ id, request }) => {
        const service = getTravelerService();
        const data = await service.updateTaskTracker(id, request);
        return { data };
      },
      invalidatesTags: ['TaskTracker'],
    }),

    deleteTaskTracker: builder.mutation<void, number>({
      queryFn: async id => {
        const service = getTravelerService();
        await service.deleteTaskTracker(id);
        return { data: undefined };
      },
      invalidatesTags: ['TaskTracker'],
    }),

    getTrussSpecificationsForBuilding: builder.query<TrussSpecificationResponse[], number>({
      queryFn: async buildingId => {
        const service = getTravelerService();
        const data = await service.getTrussSpecificationsForBuilding(buildingId);
        return { data };
      },
      providesTags: (_result, _error, buildingId) => [
        { type: 'TrussSpecification', id: buildingId },
      ],
      keepUnusedDataFor: 60,
    }),

    getAllBuildingsWithTrusses: builder.query<BuildingWithTrusses[], number[]>({
      queryFn: async buildingIds => {
        const service = getTravelerService();
        const data = await service.getAllBuildingsWithTrusses(buildingIds);
        return { data };
      },
      providesTags: (_result, _error, buildingIds) =>
        buildingIds.map(id => ({ type: 'TravelerBuildingTrusses' as const, id })),
      keepUnusedDataFor: 120,
    }),

    getManufacturerStageConfigs: builder.query<StageConfigDef[], number>({
      queryFn: async manufacturerId => {
        const service = getTravelerService();
        const data = await service.getManufacturerStageConfigs(manufacturerId);
        return { data };
      },
      providesTags: (_result, _error, manufacturerId) => [
        { type: 'StageConfig', id: manufacturerId },
      ],
      keepUnusedDataFor: 300,
    }),
  }),
});

export const {
  useGetTaskTrackersByOrderQuery,
  useGetTaskTrackersByTrussQuery,
  useCreateTaskTrackerMutation,
  useUpdateTaskTrackerMutation,
  useDeleteTaskTrackerMutation,
  useGetTrussSpecificationsForBuildingQuery,
  useGetAllBuildingsWithTrussesQuery,
  useGetManufacturerStageConfigsQuery,
} = workOrderTravelerApi;
